import * as React from 'react';
import { ITheme, TextField, useTheme } from '@mui/material';
import { Utilities } from '../../utility/utils';
import { NumericFormat, NumericFormatProps } from 'react-number-format';
import { useTranslation } from 'react-i18next';

export interface INumberInputProps {
  label?: string;
  placeholder?: string;
  value?: number;
  max?: number;
  min?: number
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, number?: number | undefined) => void;
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement, Element>) => void;
  style?: React.CSSProperties;
  smallFormat?: boolean;
  endAdornment?: string;
  startAdornment?: string;
  disableMask?: boolean;
  backgroundColor?: string;
}

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const NumericFormatCustom = React.forwardRef<NumericFormatProps, CustomProps>(
  function NumericFormatCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        maxLength={15}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
        thousandSeparator
        valueIsNumericString
      />
    );
  },
);

const NumberInput: React.FC<INumberInputProps> = ({ label, placeholder, value, min, max, onChange, onClick, onBlur, onFocus, style, smallFormat, endAdornment, startAdornment, disableMask, backgroundColor }) => {
  const theme = useTheme() as ITheme;
  const onValueChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const val = event.target.value;
    if (val === "" || val === undefined) {
      if (onChange) {
        onChange(event, undefined);
      }
      return;
    }
    let valAsNumber = parseFloat(val.replaceAll(/[^0-9,.]/g, ''));
    if (isNaN(valAsNumber)) {
      return;
    }
    if (min !== undefined && valAsNumber < min) {
      valAsNumber = min;
    }
    if (max !== undefined && valAsNumber > max) {
      valAsNumber = max;
    }
    if (onChange)
      onChange(event, valAsNumber);
  }
  return <TextField
    label={label}
    value={value !== undefined ? Utilities.round(value, 2) : ""}
    placeholder={placeholder}
    InputLabelProps={{
      shrink: true,
    }}
    onClick={onClick}
    onChange={onValueChanged}
    onBlur={onBlur}
    onFocus={onFocus}
    style={{
    }}
    aria-label={label ? label : placeholder}
    sx={{
      ".MuiInputBase-input::-webkit-outer-spin-button": {
        WebkitAppearance: "none"
      },
      ".MuiInputBase-input::-webkit-inner-spin-button": {
        WebkitAppearance: "none"
      },
      ".MuiInputBase-input": {
        MozAppearance: "textfield",
        textAlign: smallFormat ? "center" : undefined,
        padding: smallFormat ? "2px !important" : undefined
      }
    }}
    InputProps={disableMask ? {
      style: {
        ...style,
        backgroundColor: backgroundColor ? backgroundColor : theme.palette.background.paper
      },
      endAdornment: endAdornment,
      startAdornment: startAdornment,
    } : {
      inputComponent: NumericFormatCustom as any,
      inputProps: {
        defaultValue: value,
      },
      style: {
        ...style,
        backgroundColor: theme.palette.background.paper
      },
      endAdornment: endAdornment,
      startAdornment: startAdornment,
    }}
    inputProps={{
      style:{
        textAlign: smallFormat ? "center" : "right"
      }
    }}
  />;
}

// const NumberInput = React.forwardRef(function CustomNumberInput(
//   props: NumberInputProps,
//   ref: React.ForwardedRef<HTMLDivElement>,
// ) {
//   return (
//     <BaseNumberInput
//       slots={{
//         root: StyledInputRoot,
//         input: StyledInputElement,
//       }}
//       slotProps={{
//         incrementButton: {
//           hidden: true
//         },
//         decrementButton: {
//           hidden: true
//         },
//       }}
//       step={0.1}

//       {...props}
//       ref={ref}
//     />
//   );
// });

// const blue = {
//   100: '#DAECFF',
//   200: '#80BFFF',
//   400: '#3399FF',
//   500: '#007FFF',
//   600: '#0072E5',
//   700: '#0059B2',
// };

// const grey = {
//   50: '#F3F6F9',
//   100: '#E5EAF2',
//   200: '#DAE2ED',
//   300: '#C7D0DD',
//   400: '#B0B8C4',
//   500: '#9DA8B7',
//   600: '#6B7A90',
//   700: '#434D5B',
//   800: '#303740',
//   900: '#1C2025',
// };

// const StyledInputRoot = styled('div')(
//   ({ theme }) => `
//   font-weight: 400;
//   border-radius: 8px;
//   color: ${theme.palette.mode === 'dark' ? grey[300] : grey[900]};
//   background: ${theme.palette.mode === 'dark' ? grey[900] : '#fff'};
//   border: 1px solid ${theme.palette.mode === 'dark' ? grey[700] : grey[200]};

//   display: flex;
//   align-items: center;
//   overflow: hidden;
//   padding: 4px;

//   &.${numberInputClasses.focused} {
//     border-color: ${blue[400]};
//     box-shadow: 0 0 0 3px ${theme.palette.mode === 'dark' ? blue[700] : blue[200]};
//   }

//   &:hover {
//     border-color: ${blue[400]};
//   }

//   // firefox
//   &:focus-visible {
//     outline: 0;
//   }
// `,
// );

// const StyledInputElement = styled('input')(
//   ({ theme }) => `
//   font-size: 14px;
//   font-family: inherit;
//   font-weight: 400;
//   line-height: 25px;
//   grid-column: 1/2;
//   grid-row: 1/3;
//   color: ${theme.palette.mode === 'dark' ? grey[300] : grey[900]};
//   background: inherit;
//   border: none;
//   border-radius: inherit;
//   outline: 0;
// `,
// );

export default NumberInput;